import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Chip,
} from "@mui/material";
import "../../../App.css";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import { useNavigate } from "react-router-dom";
import { TableNodata } from "../../../components/TableNodata";
import { TableLoader } from "../../../components/TableLoader";
import CustomPagination from "../../../components/CustomPagination";
import useDebounce from "../../../hooks/useDebounce";
import api from "../../../api/client";
import { templateSummary } from "./paperBuilder";
import { getLabelByKey, useChangeableItems } from "store/useDynamicLabel";

const TemplatesList = () => {
  const navigate = useNavigate();

  const [showTable, setShowTable] = useState(false);
  const [listData, setListData] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [searchKey, setSearchKey] = useState("");
  const debouncedSearch = useDebounce(searchKey, 500);
  const [loading, setLoading] = useState(false);

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const headings = [
    { label: "S.No", align: "center" },
    { label: `${getLabel('TEMPLATE')} Name`, align: "left" },
    { label: `${getLabel('TEMPLATE')} Type`, align: "left" },
    { label: "Structure", align: "left" },
    { label: "Total Marks", align: "center" },
    { label: "Status", align: "center" },
    { label: "Actions", align: "center" },
  ];

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.post("ListPaperTemplates", {
        page,
        size: rowsPerPage,
        search_key: debouncedSearch || "",
      });
      const data = res?.data?.data || [];
      setListData(data);
      setCount(res?.data?.total || data.length);
    } catch (error) {
      console.log("Error fetching templates:", error);
      setListData([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setShowTable(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const handleSearch = (e) => {
    setPage(0);
    setSearchKey(e.target.value);
  };

  const handleCreate = () => navigate("/templates/create");

  const handleEdit = (template) =>
    navigate("/templates/create", { state: { edit: true, template_id: template.template_id, template } });

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {/* Header + search + create */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 2, mb: 2 }}>
        <Grid sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Box className="icon-box">
            <DashboardCustomizeIcon className="iconHeading" />
          </Box>
          <Box sx={{ ml: 2 }}>
            <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a" }}>Templates</Typography>
          </Box>
        </Grid>
        <Grid sx={{ display: "flex", gap: 4 }}>
          <TextField
            value={searchKey}
            size="small"
            variant="outlined"
            placeholder={`Search ${getLabel('TEMPLATE')}`}
            onChange={handleSearch}
            className="custom-search-field"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            className="gradient-button"
            onClick={handleCreate}
            startIcon={<AddIcon sx={{ color: "white" }} />}
          >
            <Typography sx={{ color: "white" }}>{`Create ${getLabel('TEMPLATE')}`}</Typography>
          </Button>
        </Grid>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          opacity: showTable ? 1 : 0,
          transform: showTable ? "translateY(0)" : "translateY(40px)",
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {headings.map((heading, index) => (
                <TableCell key={index} align={heading.align} className="tableHeader">
                  <b>{heading.label}</b>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody className="tablebody">
            {loading ? (
              <TableRow>
                <TableCell colSpan={headings.length} align="center" sx={{ py: 4 }}>
                  <TableLoader message="Fetching templates..." />
                </TableCell>
              </TableRow>
            ) : listData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headings.length} align="center" sx={{ py: 10 }}>
                  <TableNodata />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {listData.map((item, index) => {
                  const summary = templateSummary(item);
                  return (
                    <TableRow
                      key={item.template_id}
                      hover
                      sx={{
                        backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                        "&:hover": { backgroundColor: "#f1f1f1" },
                      }}
                    >
                      <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>

                      <TableCell>
                        <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#1a1a1a" }}>
                          {item.template_name || item.name}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#1a1a1a" }}>
                          {item.template_type || '-'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {item.parts && item.parts.length ? (
                            item.parts.map((p, pi) => (
                              <Chip
                                key={pi}
                                label={`${p.title}: ${p.count}×${p.marks} ${p.pattern === "list" ? "List" : "Either-Or"}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "10px", height: "20px", borderRadius: "4px", color: "#64748b", borderColor: "#cbd5e1" }}
                              />
                            ))
                          ) : item.parts_summary ? (
                            item.parts_summary.split("|").map((s, pi) => (
                              <Chip
                                key={pi}
                                label={s.trim()}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "10px", height: "20px", borderRadius: "4px", color: "#64748b", borderColor: "#cbd5e1" }}
                              />
                            ))
                          ) : item.parts_count != null ? (
                            <Chip
                              label={`${item.parts_count} part${item.parts_count === 1 ? "" : "s"}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "10px", height: "20px", borderRadius: "4px", color: "#64748b", borderColor: "#cbd5e1" }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: "12px", color: "#999" }}>-</Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#1a1a1a" }}>
                          {item.total_marks != null ? item.total_marks : summary.total}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 2,
                            py: 0.5,
                            borderRadius: "12px",
                            fontWeight: "bold",
                            fontSize: "12px",
                            backgroundColor:
                              item.status === "ACTIVE"
                                ? "#d4edda"
                                : item.status === "INACTIVE"
                                  ? "#f8d7da"
                                  : "#f1f1f1",
                            color:
                              item.status === "ACTIVE"
                                ? "#155724"
                                : item.status === "INACTIVE"
                                  ? "#721c24"
                                  : "#333",
                            textAlign: "center",
                          }}
                        >
                          {item.status || "—"}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                          <IconButton color="primary" onClick={() => handleEdit(item)}>
                            <EditIcon />
                            <Typography color="primary" sx={{ fontSize: "12px", ml: "4px" }}>Edit</Typography>
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {((page === 0 && count > rowsPerPage) || page !== 0) && (
                  <CustomPagination
                    page={page}
                    setPage={setPage}
                    count={count}
                    rowsPerPage={rowsPerPage}
                    colSpan={headings.length}
                  />
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TemplatesList;
